import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, LiquidityAccount, LiquidityPosition, LiquiditySeries, PlanningItem, Scenario, ScenarioHead, Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen, useBeforeUnload, useBestaetigung, useTelefon } from "@kundeninfo/ui";
import { Hinweis, quittung } from "@kundeninfo/ui/me-design";
import { sichtFuer } from "../../components/Filterkopf.js";
import { useStabileListe } from "../../components/stabileListe.js";
import { apiMeldung } from "../../components/vorgangsdetail.js";
import type { VerlassenWaechter } from "../../router.js";
import { nowOnAxis } from "../tickets/timelineModel.js";
import { AccountPanel, BalancePanel } from "./AccountPanel.js";
import { ExpectedPaymentPanel } from "./ExpectedPaymentPanel.js";
import { FinanceArea } from "./FinanceArea.js";
import { LiquidityChart } from "./LiquidityChart.js";
import { readGranularity, readScenario, rememberGranularity, rememberScenario } from "./liquidityMemory.js";
import { draftChanges, draftCounts, draftOverrides, toggleEnabled, toggleFreeze } from "./liquidityModel.js";
import type { DetailWindow, Draft } from "./liquidityModel.js";
import { LiquidityPositions } from "./LiquidityPositions.js";
import type { PlanningIntent } from "./LiquidityPositions.js";
import { LiquidityHints, LiquidityTable, LiquidityVatHint, nextVatLine } from "./LiquidityTable.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import { LiquidityToolbar } from "./LiquidityToolbar.js";
import type { ScenarioAction } from "./LiquidityToolbar.js";
import { PlanningAmountPanel, PlanningItemPanel, deleteAmountQuestion } from "./PlanningItemPanel.js";
import type { PlanningItemMode } from "./PlanningItemPanel.js";
import { ScenarioPanel } from "./ScenarioPanel.js";
import type { ScenarioPanelMode } from "./ScenarioPanel.js";
import "./Liquidity.css";

const PI = T.planningItem;
const S = T.scenario;
/** `OVERRIDES_MAX` der Route: mehr nimmt die Abfrage nicht (web importiert die Domäne nicht). */
const OVERRIDES_MAX = 50;
/** `SCENARIO_CHANGES_MAX` der Route: Darüber antwortet der Server 400 mit einem Feldnamen, den niemand lesen kann. */
const CHANGES_MAX = 200;
const NO_DRAFT: Draft = new Map();
const NO_OVERRIDES: readonly string[] = [];

export type LiquidityClient = Pick<ApiClient, "getLiquiditySeries" | "getLiquidityPosition" | "getScenarios" | "createScenario" | "updateScenario" | "setBaselineScenario"
  | "deleteScenario" | "saveScenarioPositions" | "getLiquidityAccounts" | "createLiquidityAccount" | "updateLiquidityAccount" | "addAccountBalance"
  | "getExpectedPayment" | "setExpectedPayment" | "getPlanningItems" | "createPlanningItem" | "updatePlanningItem" | "deletePlanningItem"
  | "addPlanningItemAmount" | "deletePlanningItemAmount">;

export type LiquidityAreaProps = {
  active: boolean; client: LiquidityClient; companies: readonly Unternehmen[]; activeCompanyId: string | null; userId: string | undefined;
  /** Mit Entwurf fragt die Navigation nach (Hausmuster, wie `BereichTickets`). */
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

type Dialog = { kind: "account"; account: LiquidityAccount | null } | { kind: "balance"; account: LiquidityAccount }
  | { kind: "scenario"; mode: ScenarioPanelMode } | { kind: "expectedPayment"; invoiceId: string }
  | { kind: "planningItem"; mode: PlanningItemMode } | { kind: "planningAmount"; item: PlanningItem };
/** Stammdaten der Sicht: gelesen beim Unternehmenswechsel und nach einem Schreibweg (`reloads`), nicht je Reihe. */
type Master = { companyIds: readonly string[] | undefined; reloads: number; accounts: readonly LiquidityAccount[]; scenarios: readonly Scenario[] };
type Loaded = { series: LiquiditySeries; accounts: readonly LiquidityAccount[]; scenarios: readonly Scenario[]; nowMs: number; keepPosts: boolean };

/**
 * Das Szenario, zu dem eine Planposition gehören kann: nicht die gedachte Basis, nicht „Basis je Unternehmen“ (Plan P6, 593), und auch nicht die gewählte Basis (Ruling nach
 * Aufgabe 10): Eine Position nur in der Basis fehlte in allen anderen Szenarien, eine dort angelegte Position ist allgemein.
 */
const scenarioOf = (h: ScenarioHead): { id: string; name: string } | null => (h.id === null || h.name === null || h.isBaseline ? null : { id: h.id, name: h.name });

/** Das gewählte Szenario: Ein unbekanntes (inzwischen gelöscht) fällt auf die Basis zurück. */
const selectedIn = (list: readonly Scenario[], wanted: string | null): Scenario | undefined =>
  list.find((c) => c.id === wanted) ?? list.find((c) => c.isBaseline);
/** Der Vergleich: nur ein bekanntes zweites Szenario. */
const compareIn = (list: readonly Scenario[], selected: Scenario | undefined, compareId: string | null): string | null =>
  (compareId !== selected?.id && list.some((c) => c.id === compareId) ? compareId : null);
const failure = (e: unknown, fallback: string): string => (e instanceof ApiError ? e.fields?.[0]?.message ?? apiMeldung(e, fallback) : fallback);

/**
 * DER BEREICH FINANZEN › LIQUIDITÄT (Spec „Oberfläche“; Plan P6, P9). Er füllt zwei Bereiche des Rahmens und teilt einen Zustand, deshalb
 * ein Hook wie `useBereichArtikel`: Verdichtung (gemerkt je Benutzer), Sicht der Unternehmen (R21), Szenario (gemerkt je Benutzer und Unternehmen), Vergleich, Entwurf, Fenster
 * der Detailzone, Reihe, Konten und Szenarien.
 *
 * Die Reihe lädt bei jeder Änderung neu; ein Zähler verwirft veraltete Antworten. Die alte Reihe bleibt dabei stehen: Nichts hängt aus, das
 * Diagramm zeichnet über `setOption`, die Rollposition bleibt, auch nach einem Ladefehler. Ohne `active` lädt nichts. Konten und Szenarien lesen nur der Unternehmenswechsel, die
 * Rückkehr in den Bereich und ein Schreibweg; die Szenarien zuerst, denn erst sie sagen, ob die gemerkte Wahl noch gilt.
 *
 * DER ENTWURF (Empfehlung 597, P12): Schalter und Einfrieren ändern nur `draft`, die Reihe lädt mit `override` als Vorschau (höchstens 50, darüber bleibt sie beim letzten Stand).
 * EIN „Speichern …“ fragt mit Zusammenfassung und schreibt einmal. Szenario- und Unternehmenswechsel und das Verlassen des Bereichs fragen über `VerwerfenRueckfrage`, die Verdichtung
 * und der Vergleich nicht. Ein fremder Stand lädt Szenario und Reihe neu und behält den Entwurf. Mehr als ein Unternehmen oder keine Basis: keine Schalter (593, P6).
 *
 * Am Telefon zeigt der Einspalter zuerst den Inhalt; „Positionen (n)“ öffnet die Spalte (`showColumn`), der Rückweg im Kopf (`back`) schließt
 * sie. Der Rahmen hängt das Detail dort aus: Deshalb stehen die Dialoge in dem Stück, das gerade zu sehen ist.
 */
export function useLiquidityArea(p: LiquidityAreaProps): { column: ReactNode; detail: ReactNode; showColumn: boolean;
  back: { beschriftung: string; aktion: () => void } | undefined } {
  const phone = useTelefon();
  const [chosen, setChosen] = useState<LiquiditySeries["granularity"] | null>(null);
  const granularity = useMemo(() => chosen ?? readGranularity(p.userId, phone), [chosen, p.userId, phone]);
  const [extra, setExtra] = useState<string[]>([]);
  const companyIds = useStabileListe(sichtFuer(p.activeCompanyId, extra));
  /** Nur bei genau einem Unternehmen gibt es eine Wahl (593). */
  const single = companyIds === undefined ? p.activeCompanyId : companyIds.length === 1 ? companyIds[0] ?? null : null;
  /** `undefined`: noch keine Wahl in dieser Sicht, es gilt die gemerkte. `null`: die Basis. */
  const [scenarioChoice, setScenarioChoice] = useState<string | null | undefined>(undefined);
  const wanted = useMemo(() => (scenarioChoice !== undefined ? scenarioChoice : single === null ? null : readScenario(p.userId, single)), [scenarioChoice, single, p.userId]);
  const [compareId, setCompareId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(NO_DRAFT);
  /** Der neueste Stand: Zwei Griffe vor dem nächsten Rendern dürfen sich nicht überschreiben. */
  const latestDraft = useRef<Draft>(NO_DRAFT);
  /** Was die Reihe gerade als Vorschau trägt: bleibt über 50 beim letzten Stand. */
  const [preview, setPreview] = useState<readonly string[]>(NO_OVERRIDES);
  const [writing, setWriting] = useState(false);
  /** Hinweis nach einem fremden Stand: Er überlebt das Neuladen, das `error` räumt. */
  const [notice, setNotice] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const [detailWindow, setDetailWindow] = useState<DetailWindow>(null);
  const [frameMode, setFrameMode] = useState(false);
  const [columnOpen, setColumnOpen] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  /** Ein Panel aus der Spalte hat gespeichert: Die Spalte gibt den Fokus mit der nächsten Reihe an den Auslöser zurück. */
  const [panelSaved, setPanelSaved] = useState(false);
  const requests = useRef(0);
  const master = useRef<Master | null>(null);
  /** Wofür die zuletzt angekommenen Posten der Spalte gelten: Szenario und Stand der Daten. */
  const postsFor = useRef<string | null>(null);
  /** Verwirft die Antwort auf das Öffnen einer Planposition, wenn inzwischen eine neue gefragt ist oder der Bereich verlassen wurde. */
  const planningRequests = useRef(0);
  const { bestaetigen } = useBestaetigung();

  useEffect(() => {
    if (!p.active) return;
    const request = ++requests.current;
    const scope = companyIds === undefined ? {} : { companyIds };
    const known = master.current;
    const head: Promise<Master> = known !== null && known.companyIds === companyIds && known.reloads === reloads ? Promise.resolve(known)
      : Promise.all([p.client.getLiquidityAccounts(scope), p.client.getScenarios(scope)])
        .then(([a, c]) => ({ companyIds, reloads, accounts: a.items, scenarios: c.items }));
    head.then((m) => {
      if (request !== requests.current) return undefined;
      master.current = m;
      if (single !== null && wanted !== null && !m.scenarios.some((c) => c.id === wanted)) rememberScenario(p.userId, single, null);
      const selected = selectedIn(m.scenarios, wanted);
      const scenario = selected?.isBaseline === false ? selected.id : null, compare = compareIn(m.scenarios, selected, compareId);
      return p.client.getLiquiditySeries({ granularity, ...scope, ...(scenario === null ? {} : { scenario }), ...(compare === null ? {} : { compare }),
        ...(preview.length === 0 ? {} : { overrides: preview }) }).then((series) => {
        if (request !== requests.current) return;
        const posts = `${reloads}|${String(companyIds)}|${scenario}`;
        setLoaded({ series, accounts: m.accounts, scenarios: m.scenarios, nowMs: nowOnAxis(series.today, new Date()), keepPosts: postsFor.current === posts });
        postsFor.current = posts;
        setError(null);
      });
    }).catch((e: unknown) => {
      if (request !== requests.current) return;
      setError(apiMeldung(e, T.loadFailed));
      setPanelSaved(false);
    });
  }, [p.active, p.client, p.userId, granularity, companyIds, single, wanted, compareId, preview, reloads]);

  // Beim Verlassen fällt weg, was keine Adresse hat (Muster `useBereichArtikel`): Der Hook lebt in App, ein offener Dialog stünde sonst beim
  // Zurückkommen wieder da. Fenster und zugeschaltete Unternehmen bleiben: Sie sind in der Leiste sichtbar und tragen den Rückweg aus der Akte.
  useEffect(() => {
    if (p.active) return;
    setDialog(null);
    setError(null);
    setFrameMode(false);
    setColumnOpen(false);
    applyDraft(NO_DRAFT);
    master.current = null;
    postsFor.current = null;
    requests.current += 1;
    planningRequests.current += 1;
  }, [p.active]);

  const hasDraft = draft.size > 0;
  useBeforeUnload(hasDraft);
  const guard = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!hasDraft || guard === undefined) return undefined;
    return guard(async () => {
      const leave = await darfAbbrechen(true);
      if (leave) discardDraft();
      return leave;
    });
  }, [hasDraft, guard]);

  useEffect(() => { setPanelSaved(false); }, [loaded]);

  // Die Quittung erst, wenn der Dialog zu ist: `quittung()` legte sie sonst in ihn, und sie hinge mit ihm aus.
  useEffect(() => {
    if (dialog !== null || receipt === null) return;
    quittung({ text: receipt });
    setReceipt(null);
  }, [dialog, receipt]);

  /** Lädt Stammdaten und Reihe im Stehen neu; `null` ohne Quittung (die gab schon das Objektpanel des Gastgebers, PF13). */
  const reload = (text: string | null): void => {
    if (text !== null) setReceipt(text);
    setReloads((n) => n + 1);
  };
  const saved = (text: string | null): void => {
    setDialog(null);
    reload(text);
  };
  const savedFromColumn = (text: string | null): void => {
    setPanelSaved(true);
    saved(text);
  };
  const chooseGranularity = (g: LiquiditySeries["granularity"]): void => {
    rememberGranularity(p.userId, g);
    setChosen(g);
    setFrameMode(false);
  };
  const chooseWindow = (w: DetailWindow): void => {
    setDetailWindow(w);
    setFrameMode(false);
  };

  function applyDraft(next: Draft): void {
    latestDraft.current = next;
    setDraft(next);
    if (next.size === 0) setNotice(null);
    const overrides = draftOverrides(next);
    if (overrides.length <= OVERRIDES_MAX) setPreview((old) => (old.join() === overrides.join() ? old : overrides));
  }
  function discardDraft(): void {
    applyDraft(NO_DRAFT);
  }
  /** Was den Entwurf verlöre, fragt zuerst; „Weiter bearbeiten“ lässt alles stehen. Das Menü zieht die Hülle auf seinen Wert zurück. */
  async function leavingDraft(then: () => void): Promise<void> {
    if (draft.size > 0) {
      if (!(await darfAbbrechen(true))) return;
      discardDraft();
    }
    then();
  }
  /** Die Basis merkt sich als „nichts gemerkt“: Wird später ein anderes Szenario zur Basis, gilt das. */
  const pick = (c: Scenario): void => {
    const id = c.isBaseline ? null : c.id;
    rememberScenario(p.userId, c.companyId, id);
    setScenarioChoice(id);
    setNotice(null);
    if (compareId === c.id) setCompareId(null);
  };
  const chooseScenario = (id: string): void => {
    if (writing) return;
    const picked = loaded?.scenarios.find((c) => c.id === id);
    if (picked !== undefined) void leavingDraft(() => pick(picked));
  };
  const chooseCompanies = (ids: string[]): void => {
    if (writing) return;
    void leavingDraft(() => {
      setExtra(ids);
      setScenarioChoice(undefined);
      setCompareId(null);
    });
  };

  /** Ein Schreibweg aus der Leiste. Ein fremder Stand lädt Szenario und Reihe neu und sagt es; jeder andere Befund steht nur da, der Aufruf kann wiederholt werden. */
  async function writeScenario(run: () => Promise<unknown>, done: () => void, conflict: string, fallback: string): Promise<void> {
    setWriting(true);
    setNotice(null);
    try {
      await run();
      done();
    } catch (e) {
      if (e instanceof ApiError && e.code === "VERSIONSKONFLIKT") {
        setNotice(conflict);
        setReloads((n) => n + 1);
      } else {
        setError(failure(e, fallback));
      }
    } finally {
      setWriting(false);
    }
  }
  async function saveDraft({ id, version, name }: ScenarioHead): Promise<void> {
    if (id === null || version === null || name === null) return;
    if (!(await bestaetigen({ titel: T.draft.confirmTitle, bestaetigenText: T.draft.confirmSave, text: T.draft.confirm(name, draftCounts(draft)) }))) return;
    await writeScenario(() => p.client.saveScenarioPositions(id, { version, changes: draftChanges(draft) }), () => {
      discardDraft();
      reload(T.draft.saved);
    }, T.draft.conflict, T.draft.saveFailed);
  }
  async function setBaseline(c: Scenario): Promise<void> {
    if (!(await bestaetigen({ titel: S.baselineTitle, bestaetigenText: S.baselineConfirm, text: S.baselineText(c.name) }))) return;
    await writeScenario(() => p.client.setBaselineScenario(c.id, { version: c.version }), () => {
      rememberScenario(p.userId, c.companyId, null);
      reload(S.baselineSaved);
    }, S.conflict, S.actionFailed);
  }
  async function deleteScenario(c: Scenario): Promise<void> {
    if (!(await bestaetigen({ titel: S.deleteTitle, bestaetigenText: S.deleteConfirm, gefaehrlich: true, text: S.deleteText(c.name, draft.size > 0) }))) return;
    await writeScenario(() => p.client.deleteScenario(c.id, c.version), () => {
      discardDraft();
      rememberScenario(p.userId, c.companyId, null);
      setScenarioChoice(null);
      reload(S.deleted);
    }, S.conflict, S.actionFailed);
  }
  const scenarioSaved = (mode: ScenarioPanelMode, c: Scenario): void => {
    if (mode.kind !== "edit") {
      discardDraft();
      pick(c);
    }
    saved(S.saved);
  };
  const onScenarioAction = (a: ScenarioAction, s: LiquiditySeries): void => {
    if (writing) return;
    const current = loaded?.scenarios.find((c) => c.id === s.scenario.id);
    if (a === "create") void leavingDraft(() => setDialog({ kind: "scenario", mode: { kind: "create" } }));
    else if (current === undefined) return;
    else if (a === "duplicate") void leavingDraft(() => setDialog({ kind: "scenario", mode: { kind: "duplicate", from: current } }));
    else if (a === "edit") setDialog({ kind: "scenario", mode: { kind: "edit", scenario: current } });
    else if (current.isBaseline) return;
    else if (a === "baseline") void setBaseline(current);
    else void deleteScenario(current);
  };
  const onDraftAction = (a: "save" | "saveAsNew" | "discard", s: LiquiditySeries): void => {
    if (writing) return;
    if (a !== "discard" && draft.size > CHANGES_MAX) {
      setNotice(T.draft.tooMany(draft.size, CHANGES_MAX));
      return;
    }
    if (a === "discard") discardDraft();
    else if (a === "save") void saveDraft(s.scenario);
    else setDialog({ kind: "scenario", mode: { kind: "fromDraft", from: loaded?.scenarios.find((c) => c.id === s.scenario.id) ?? null, changes: draftChanges(draft) } });
  };

  /**
   * Die Reihe kennt keine Version: Ändern, „Betrag ändern ab …“ und Betrag löschen holen den Stand erst jetzt. Eine inzwischen entfernte
   * Planposition ist kein Fehler, nur eine veraltete Spalte: Quittung und neue Reihe.
   */
  async function openPlanning(i: Exclude<PlanningIntent, { kind: "new" }>): Promise<void> {
    const asked = ++planningRequests.current;
    setError(null);
    if (i.kind === "deleteAmount" && !(await bestaetigen(deleteAmountQuestion(i.amount)))) return;
    const scenarioId = loaded?.series.scenario.id ?? null;
    try {
      const { items } = await p.client.getPlanningItems({ companyIds: [i.position.companyId], ...(scenarioId === null ? {} : { scenarioId }) });
      if (asked !== planningRequests.current) return;
      const item = items.find((x) => x.id === i.position.id);
      if (item === undefined) savedFromColumn(PI.gone);
      else if (i.kind === "edit") setDialog({ kind: "planningItem", mode: { kind: "edit", item } });
      else if (i.kind === "changeAmount") setDialog({ kind: "planningAmount", item });
      else {
        await p.client.deletePlanningItemAmount(item.id, i.amount.id, item.version);
        savedFromColumn(PI.amountDeleted);
      }
    } catch (e) {
      if (asked !== planningRequests.current) return;
      setError(failure(e, PI.loadFailed));
    }
  }
  const onPlanning = (i: PlanningIntent, s: LiquiditySeries): void => {
    if (i.kind !== "new") {
      void openPlanning(i);
      return;
    }
    const companyId = p.activeCompanyId ?? s.companyIds[0];
    if (companyId !== undefined) setDialog({ kind: "planningItem", mode: { kind: "create", group: i.group, companyId } });
  };

  const series = loaded?.series ?? null, accounts = loaded?.accounts ?? [], scenarios = loaded?.scenarios ?? [];
  const selected = selectedIn(scenarios, wanted);
  /** Bei mehr als einem Unternehmen und ohne Basis trägt die Reihe keine Kennung des Szenarios (`headOf` der Domäne, 593, P6): dann keine Schalter. */
  const editable = series !== null && series.scenario.id !== null;
  /** Die Wahl gibt es nicht mehr, die Reihe zeigt die Basis: Ein Entwurf gilt nun dort, und das muss dastehen, bevor jemand ihn speichert. */
  const draftMoved = hasDraft && series !== null && wanted !== null && !scenarios.some((c) => c.id === wanted);
  const showColumn = phone && columnOpen;
  /** Der Name des Unternehmens, um das es im Dialog geht, wenn die Sicht mehrere zeigt. */
  const newCompanyName = (s: LiquiditySeries, companyId: string | null): string | null =>
    (s.companyIds.length > 1 ? p.companies.find((c) => c.id === companyId)?.name ?? null : null);
  const panelOf = (d: Dialog, s: LiquiditySeries): ReactElement => {
    switch (d.kind) {
      case "account":
        return <AccountPanel client={p.client} account={d.account} companyId={p.activeCompanyId} onCancel={() => setDialog(null)}
          companyName={newCompanyName(s, p.activeCompanyId)} onSaved={() => saved(T.dialogs.account.saved)} />;
      case "balance":
        return <BalancePanel client={p.client} account={d.account} today={s.today} onCancel={() => setDialog(null)} onSaved={() => saved(T.dialogs.balance.saved)} />;
      case "scenario":
        return <ScenarioPanel mode={d.mode} companyId={s.scenario.companyId ?? p.activeCompanyId ?? s.companyIds[0] ?? ""} client={p.client} onCancel={() => setDialog(null)}
          onSaved={(c) => scenarioSaved(d.mode, c)} />;
      case "expectedPayment":
        return <ExpectedPaymentPanel client={p.client} invoiceId={d.invoiceId} onCancel={() => setDialog(null)}
          onSaved={(v) => savedFromColumn(T.expectedPayment.saved(v.expected !== null))} />;
      case "planningItem":
        return <PlanningItemPanel mode={d.mode} scenario={scenarioOf(s.scenario)} client={p.client} onCancel={() => setDialog(null)} onSaved={savedFromColumn} onAmountsChanged={reload}
          companyName={newCompanyName(s, d.mode.kind === "create" ? d.mode.companyId : d.mode.item.companyId)} vatHint={nextVatLine(s)} />;
      case "planningAmount":
        return <PlanningAmountPanel item={d.item} client={p.client} onCancel={() => setDialog(null)} onSaved={() => savedFromColumn(PI.amountSaved)} />;
    }
  };
  const dialogs = dialog === null || series === null ? null : panelOf(dialog, series);

  const column = (
    <>
      {series === null ? (error === null && <p className="liq__muted" role="status">{T.loading}</p>) : (
        <LiquidityPositions series={series} accounts={accounts} client={p.client} scenarioId={series.scenario.id} keepPosts={loaded?.keepPosts ?? false}
          onGeaendert={() => savedFromColumn(null)}
          {...(editable ? { editable: true as const, draft, locked: writing || selected?.id !== series.scenario.id, onToggleEnabled: (x: LiquidityPosition) => applyDraft(toggleEnabled(latestDraft.current, x)),
            onToggleFreeze: (x: LiquidityPosition) => applyDraft(toggleFreeze(latestDraft.current, x)) } : { editable: false as const })}
          onPlanning={(i) => onPlanning(i, series)}
          companies={p.companies}
          onAccount={(id) => setDialog({ kind: "account", account: id === null ? null : accounts.find((a) => a.id === id) ?? null })}
          onBalance={(id) => {
            const account = accounts.find((a) => a.id === id);
            if (account !== undefined) setDialog({ kind: "balance", account });
          }}
          onExpectedPayment={(invoiceId) => setDialog({ kind: "expectedPayment", invoiceId })} panelSaved={panelSaved} />
      )}
      {showColumn && error !== null && <Hinweis art="fehler">{error}</Hinweis>}
      {showColumn && dialogs}
    </>
  );

  const detail = (
    <FinanceArea>
      <div className="liq__content">
        <LiquidityToolbar series={series} scenarios={scenarios} granularity={granularity} onGranularity={chooseGranularity}
          selectedScenarioId={selected?.id ?? null} compareId={compareIn(scenarios, selected, compareId)} onScenario={chooseScenario} onCompare={setCompareId}
          onScenarioAction={(a) => { if (series !== null) onScenarioAction(a, series); }} draftCount={draft.size}
          previewLimit={draftOverrides(draft).length > OVERRIDES_MAX ? OVERRIDES_MAX : null} onDraftAction={(a) => { if (series !== null) onDraftAction(a, series); }}
          companies={p.companies} activeCompanyId={p.activeCompanyId} extraCompanies={extra} onExtraCompanies={chooseCompanies}
          window={detailWindow} onWindow={chooseWindow} frameMode={frameMode} onFrameMode={setFrameMode} phone={phone}
          onShowPositions={() => setColumnOpen(true)} />
        {error !== null && <Hinweis art="fehler">{error}</Hinweis>}
        {notice !== null && <Hinweis art="warnung">{notice}</Hinweis>}
        {draftMoved && <Hinweis art="warnung">{T.draft.scenarioGone(series.scenario.name ?? T.toolbar.baseline)}</Hinweis>}
        {series === null ? (error === null && <p className="liq__muted" role="status">{T.loading}</p>) : (
          <>
            <LiquidityHints series={series} accounts={accounts} companies={p.companies} onGeaendert={() => reload(null)} />
            <LiquidityChart series={series} window={detailWindow} frameMode={frameMode} phone={phone} nowMs={loaded?.nowMs ?? 0} onWindow={chooseWindow} />
            <LiquidityTable series={series} window={detailWindow} phone={phone} />
            <LiquidityVatHint series={series} />
          </>
        )}
      </div>
      {!showColumn && dialogs}
    </FinanceArea>
  );

  return { column, detail, showColumn,
    back: showColumn ? { beschriftung: T.toolbar.backToContent, aktion: () => setColumnOpen(false) } : undefined };
}
